import { useState, useRef, useEffect, useCallback } from 'react'
import ChatMessage from '../components/ChatMessage'
import ChatInput from '../components/ChatInput'
import TypingIndicator from '../components/TypingIndicator'

// Coordinator URL — MUST be set via VITE_COORDINATOR_URL env var.
// No fallback — the Tailscale IP must never be in publicly deployed code.
// On Railway: leave unset (chat disabled for public users).
// Locally: set VITE_COORDINATOR_URL=http://100.120.209.60:7777 in .env.local
const COORDINATOR_URL =
  (import.meta as any).env?.VITE_COORDINATOR_URL ?? ''

// localStorage key for persisting the active conversation across page reloads
const CONV_STORAGE_KEY = 'rethinker_chat_conv_id'

// Shape used internally and by ChatMessage component
interface DisplayMessage {
  id: string
  role: 'user' | 'agent'
  content: string
  timestamp: Date
}

// Shape returned by GET /chat/history/{id}
interface CoordMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

function coordToDisplay(m: CoordMessage, idx: number): DisplayMessage {
  return {
    id: `hist-${idx}`,
    role: m.role === 'user' ? 'user' : 'agent',
    content: m.content,
    timestamp: new Date(m.timestamp),
  }
}

function newConversationId(): string {
  // Generate a 12-char hex ID matching the coordinator's uuid4().hex[:12] format
  return Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export default function ChatPage() {
  const [convId, setConvId] = useState<string>(() => {
    // Restore previous conversation from localStorage, or start fresh
    return localStorage.getItem(CONV_STORAGE_KEY) ?? newConversationId()
  })
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [isWaiting, setIsWaiting] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Persist conversation ID whenever it changes
  useEffect(() => {
    localStorage.setItem(CONV_STORAGE_KEY, convId)
  }, [convId])

  // On mount (or when convId changes): load history from coordinator
  useEffect(() => {
    let cancelled = false

    async function loadHistory() {
      setInitError(null)
      try {
        const res = await fetch(`${COORDINATOR_URL}/chat/history/${convId}`)
        if (!res.ok) {
          // 404 or no history yet is fine — just start empty
          if (res.status !== 404) {
            throw new Error(`History fetch failed: ${res.status}`)
          }
          return
        }
        const data: { conversation_id: string; messages: CoordMessage[] } =
          await res.json()
        if (!cancelled) {
          setMessages(data.messages.map(coordToDisplay))
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load chat history:', err)
          setInitError('Could not reach the coordinator. Is it running on port 7777?')
        }
      }
    }

    loadHistory()
    return () => { cancelled = true }
  }, [convId])

  // Start a fresh conversation — clear local state + generate new ID
  const handleNewConversation = useCallback(() => {
    const id = newConversationId()
    setMessages([])
    setInitError(null)
    setConvId(id)
  }, [])

  const handleSendMessage = useCallback(async (content: string) => {
    // Optimistically append the user message
    const tempUserMsg: DisplayMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, tempUserMsg])
    setIsWaiting(true)

    try {
      const res = await fetch(`${COORDINATOR_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content, conversation_id: convId }),
      })

      if (!res.ok) {
        throw new Error(`Coordinator returned ${res.status}`)
      }

      const data: { response: string; conversation_id: string } = await res.json()

      // Append the assistant reply
      const assistantMsg: DisplayMessage = {
        id: `resp-${Date.now()}`,
        role: 'agent',
        content: data.response,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, assistantMsg])

      // Sync conversation_id in case the coordinator generated one (shouldn't
      // differ, but keeps state authoritative)
      if (data.conversation_id && data.conversation_id !== convId) {
        setConvId(data.conversation_id)
      }
    } catch (err) {
      console.error('Failed to send message:', err)
      const errorMsg: DisplayMessage = {
        id: `error-${Date.now()}`,
        role: 'agent',
        content: 'Failed to reach the coordinator. Please check that it is running.',
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, errorMsg])
    } finally {
      setIsWaiting(false)
    }
  }, [convId])

  if (initError) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <p className="text-red-400">{initError}</p>
          <button
            onClick={handleNewConversation}
            className="text-sm text-gray-400 hover:text-gray-200 underline"
          >
            Start new conversation
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header row with conversation ID + New Conversation button */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 text-xs text-gray-500">
        <span>Conversation: <code className="text-gray-400">{convId}</code></span>
        <button
          onClick={handleNewConversation}
          disabled={isWaiting}
          className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300 transition-colors"
        >
          New Conversation
        </button>
      </div>

      {/* Empty state */}
      {messages.length === 0 && !isWaiting && (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-2xl text-center space-y-4">
            <h1 className="text-3xl font-bold text-gray-100">
              ReThinker Orchestrator
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Talk to the orchestrator. It has access to system health, Todoist,
              Docker, and the GPU. Responses run locally via claude CLI — no cloud cost.
            </p>
          </div>
        </div>
      )}

      {/* Message history */}
      {(messages.length > 0 || isWaiting) && (
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {messages.map(message => (
            <ChatMessage key={message.id} message={message} />
          ))}
          {isWaiting && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input area — pb-[env(safe-area-inset-bottom)] keeps it above iOS home indicator */}
      <div className="border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <ChatInput
            onSend={handleSendMessage}
            disabled={isWaiting}
          />
        </div>
      </div>
    </div>
  )
}
